import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import { money } from "../../lib/api";
import { Loading } from "../../components/States";
import { PageHeader } from "../../components/PageHeader";
import { StageBar, StatusPill } from "../../components/StageBar";
import { useNavigate } from "react-router-dom";
import { MapPin, Home, CalendarCheck, ChevronRight } from "lucide-react";

const TABS = ["Communities", "Lots", "Inspections"];

export default function Projects() {
  const communities = useFetch("/communities");
  const lots = useFetch("/lots");
  const inspections = useFetch("/inspections");
  const builders = useFetch("/builders");
  const [tab, setTab] = useState("Communities");
  const navigate = useNavigate();

  if (communities.loading || lots.loading || inspections.loading) return <Loading />;

  const builderName = Object.fromEntries((builders.data || []).map((b) => [b.id, b.name]));
  const commName = Object.fromEntries((communities.data || []).map((c) => [c.id, c.name]));

  const lotsFor = (cid) => (lots.data || []).filter((l) => l.community_id === cid);

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Production Operations" title="Projects" subtitle="Communities, lots and inspections across every builder." />

      <div className="flex gap-2 border-b border-slate-800">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-3 text-sm font-semibold transition ${tab === t ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-white"}`}
            data-testid={`projects-tab-${t.toLowerCase()}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Communities" && (
        <div className="grid gap-5 md:grid-cols-2">
          {(communities.data || []).map((c) => {
            const cl = lotsFor(c.id);
            const active = cl.filter((l) => l.stage !== "final").length;
            return (
              <div key={c.id} className="hw-card overflow-hidden hw-rise" data-testid={`community-${c.id}`}>
                <div className="relative h-32 bg-gradient-to-br from-cyan-900/40 to-slate-900">
                  <div className="absolute inset-0 grid-texture opacity-50" />
                  <div className="absolute bottom-3 left-5">
                    <div className="hw-eyebrow">{builderName[c.builder_id]}</div>
                    <div className="font-head text-2xl font-bold text-white">{c.name}</div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between text-sm text-slate-400">
                    <span className="flex items-center gap-1.5"><MapPin size={14} />{c.city}</span>
                    <span>{c.jurisdiction}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <div><div className="font-head text-2xl font-bold text-white">{cl.length}</div><div className="text-xs text-slate-500">Lots wired</div></div>
                    <div><div className="font-head text-2xl font-bold text-cyan-400">{active}</div><div className="text-xs text-slate-500">Active</div></div>
                    <div><div className="font-head text-2xl font-bold text-emerald-300">{c.lots_total}</div><div className="text-xs text-slate-500">In community</div></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "Lots" && (
        <div className="space-y-3">
          {(lots.data || []).map((l) => (
            <div
              key={l.id}
              onClick={() => navigate(`/app/projects/lot/${l.id}`)}
              className="hw-card cursor-pointer p-5 transition hover:border-cyan-500/40 hw-rise"
              data-testid={`lot-${l.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-300"><Home size={20} /></div>
                  <div>
                    <div className="font-semibold text-white">{commName[l.community_id]} · {l.label}</div>
                    <div className="text-sm text-slate-400">{l.address}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="hidden font-head text-lg font-bold text-cyan-400 sm:block">{money(l.contract_price)}</span>
                  <StatusPill status={l.inspection_status} />
                  <ChevronRight size={18} className="text-slate-600" />
                </div>
              </div>
              <div className="mt-4"><StageBar stage={l.stage} /></div>
            </div>
          ))}
        </div>
      )}

      {tab === "Inspections" && (
        <div className="space-y-3">
          {(inspections.data || []).map((i) => (
            <div key={i.id} className="hw-card p-5 hw-rise" data-testid={`inspection-${i.id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${i.result === "failed" ? "bg-rose-500/15 text-rose-300" : i.result === "passed" ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"}`}><CalendarCheck size={20} /></div>
                  <div>
                    <div className="font-semibold text-white">{commName[i.community_id]} · {i.lot_label} — {i.type} Inspection</div>
                    <div className="text-sm text-slate-400">{i.jurisdiction} · Inspector {i.inspector} · {i.electrician}</div>
                    {i.failure_reason && <div className="mt-1 text-sm text-rose-300">⚠ {i.failure_reason}</div>}
                  </div>
                </div>
                <StatusPill status={i.result} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
